import { ENTITY_REFERENCE_TYPE } from 'app/shared/components/TeamChat/TeamChat.entityReferences.utils';
import { createSelector } from 'reselect';

import { occupancyMap, propertyStatusMap } from 'constants/options';
import { getBorrowerFullName } from 'utils/loanApplication';
import { formatNumberToCurrencyString } from 'utils/number';

import { selectLoanApplicationFinancialInfo } from './loanApplication.selectors';

const getOwnerNames = ownersFullNames =>
  (ownersFullNames ?? []).map(getBorrowerFullName).filter(Boolean).join(' / ');

const selectFinancialInfoSummary = createSelector(
  selectLoanApplicationFinancialInfo,
  financialInfo => financialInfo?.summary ?? {},
);

const selectFinancialInfoIncomes = createSelector(
  selectLoanApplicationFinancialInfo,
  financialInfo => financialInfo?.incomes ?? [],
);

const selectFinancialInfoAssets = createSelector(
  selectLoanApplicationFinancialInfo,
  financialInfo => financialInfo?.assets ?? [],
);

const selectFinancialInfoLiabilities = createSelector(
  selectLoanApplicationFinancialInfo,
  financialInfo => financialInfo?.liabilities ?? [],
);

const selectFinancialInfoProperties = createSelector(
  selectLoanApplicationFinancialInfo,
  financialInfo => financialInfo?.properties ?? [],
);

/**
 * `financialInfo.summary.applicationId` doubles as the "financial data is loaded for loan X"
 * signal — the entity-reference chip (DEV-5446) compares this against its own chat's loan id
 * before treating a missing candidate as a tombstone, so it never false-tombstones while the
 * Financial Info fetch is still in flight or hasn't been triggered yet.
 */
export const selectEntityReferenceLoadedApplicationId = createSelector(
  selectFinancialInfoSummary,
  summary => summary?.applicationId ?? null,
);

const selectBorrowerCandidates = createSelector(selectFinancialInfoSummary, summary =>
  (summary?.borrowers ?? []).map(borrower => {
    const fullName = getBorrowerFullName(borrower.borrowerFullName);
    const borrowerType = borrower.isPrimary ? 'Primary' : 'Co-Borrower';

    return {
      entityType: ENTITY_REFERENCE_TYPE.BORROWER,
      entityId: borrower.id,
      label: fullName,
      secondary: borrowerType,
      ownerName: fullName,
      details: { fullName, borrowerType },
    };
  }),
);

const selectIncomeCandidates = createSelector(selectFinancialInfoIncomes, incomes =>
  incomes.map(income => ({
    entityType: ENTITY_REFERENCE_TYPE.INCOME,
    entityId: income.id,
    label: `${income.incomeType} ${formatNumberToCurrencyString(income.monthlyIncome ?? 0)}`,
    secondary: income.employerName ?? '',
    ownerName: getBorrowerFullName(income.borrowerFullName),
    details: {
      incomeTypeName: income.incomeType,
      monthlyIncome: income.monthlyIncome,
      employerName: income.employerName ?? '',
    },
  })),
);

const selectAssetCandidates = createSelector(selectFinancialInfoAssets, assets =>
  assets.map(asset => ({
    entityType: ENTITY_REFERENCE_TYPE.ASSET,
    entityId: asset.id,
    label: `${asset.assetTypeName || 'Asset'} ${formatNumberToCurrencyString(asset.balance ?? 0)}`,
    secondary: asset.institution ?? '',
    ownerName: getOwnerNames(asset.ownersFullNames),
    details: {
      institution: asset.institution ?? '',
      balance: asset.balance,
      isVerified: !!asset.isVerified,
    },
  })),
);

const selectLiabilityCandidates = createSelector(selectFinancialInfoLiabilities, liabilities =>
  liabilities.map(liability => ({
    entityType: ENTITY_REFERENCE_TYPE.LIABILITY,
    entityId: liability.id,
    label: `${liability.creditorName || 'Liability'} ${formatNumberToCurrencyString(
      liability.unpaidBalance ?? 0,
    )}`,
    secondary: liability.liabilityAccountType ?? '',
    ownerName: getOwnerNames(liability.ownersFullNames),
    details: {
      unpaidBalance: liability.unpaidBalance,
      monthlyPayment: liability.monthlyPayment,
      payoffRequired: !!liability.payoffRequired,
    },
  })),
);

const getReoLabel = address => address?.split(',')[0]?.trim() || 'Property';

const getReoDisplayValue = (map, key) => map[key]?.name ?? key ?? '';

const selectReoCandidates = createSelector(selectFinancialInfoProperties, properties =>
  properties.map(property => {
    const occupancy = getReoDisplayValue(occupancyMap, property.occupancyType?.type);
    const status = getReoDisplayValue(propertyStatusMap, property.status);

    return {
      entityType: ENTITY_REFERENCE_TYPE.REO,
      entityId: property.id,
      label: getReoLabel(property.address),
      secondary: [occupancy, status].filter(Boolean).join(' · '),
      ownerName: getOwnerNames(property.ownersFullNames),
      details: {
        address: property.address,
        status,
        occupancy,
        netIncome: property.netIncome ?? null,
        isSubjectProperty: !!property.isSubjectProperty,
      },
    };
  }),
);

// Liability + REO are combined into one intermediate stage first so the final combiner below stays
// within the repo's max-params: 4 (an eslint hard rule) — one param per remaining type.
const selectLiabilityAndReoCandidates = createSelector(
  [selectLiabilityCandidates, selectReoCandidates],
  (liability, reo) => ({ liability, reo }),
);

/**
 * One aggregated, per-type candidate list for the loan-chat `#` entity picker AND the inline
 * entity-reference chip's hover card (DEV-5446). Sourced from the SAME `financialInfo` slice the
 * redesigned Financial Info tab renders from, so a candidate's `label`/`secondary` match what the
 * user sees on that tab. `details` carries the extra fields the chip's hover card needs beyond
 * the picker's label/secondary — kept in one aggregation so both surfaces read the same data.
 */
export const selectEntityReferenceCandidatesByType = createSelector(
  [
    selectBorrowerCandidates,
    selectIncomeCandidates,
    selectAssetCandidates,
    selectLiabilityAndReoCandidates,
  ],
  (borrower, income, asset, { liability, reo }) => ({
    [ENTITY_REFERENCE_TYPE.BORROWER]: borrower,
    [ENTITY_REFERENCE_TYPE.INCOME]: income,
    [ENTITY_REFERENCE_TYPE.ASSET]: asset,
    [ENTITY_REFERENCE_TYPE.LIABILITY]: liability,
    [ENTITY_REFERENCE_TYPE.REO]: reo,
  }),
);

// Resolves a single candidate's current label by id — used by the "Discuss in chat" row action
// (DEV-5448) so the token it inserts matches what the picker/hover-card already show, instead of
// re-deriving the same currency/name formatting a second time in the row's own component.
export const selectEntityReferenceLabel =
  ({ entityType, entityId }) =>
  state => {
    const candidates = selectEntityReferenceCandidatesByType(state)?.[entityType] ?? [];

    return candidates.find(candidate => candidate.entityId === entityId)?.label ?? '';
  };
